import type { EditorView } from "@codemirror/view";

export function wrapSelection(view: EditorView, before: string, after: string) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const selected = state.sliceDoc(from, to);

  view.dispatch({
    changes: { from, to, insert: `${before}${selected}${after}` },
    selection: { anchor: from + before.length, head: from + before.length + selected.length },
  });
  view.focus();
}

export function insertSnippet(view: EditorView, text: string, cursorOffset?: number) {
  const { from, to } = view.state.selection.main;
  view.dispatch({
    changes: { from, to, insert: text },
    selection: { anchor: from + (cursorOffset ?? text.length) },
  });
  view.focus();
}

export function wrapBlock(view: EditorView, envName: string) {
  const { from, to } = view.state.selection.main;
  const selected = view.state.sliceDoc(from, to);
  const open = `\\begin{${envName}}\n`;
  const close = `\n\\end{${envName}}`;
  const body = selected || "  ";

  view.dispatch({
    changes: { from, to, insert: `${open}${body}${close}` },
    selection: { anchor: from + open.length, head: from + open.length + body.length },
  });
  view.focus();
}

export function toggleLinePrefix(view: EditorView, prefix: string) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const startLine = state.doc.lineAt(from).number;
  const endLine = state.doc.lineAt(to).number;

  const changes: { from: number; to: number; insert: string }[] = [];
  let allPrefixed = true;

  for (let i = startLine; i <= endLine; i++) {
    const line = state.doc.line(i);
    if (!line.text.startsWith(prefix)) {
      allPrefixed = false;
      break;
    }
  }

  for (let i = startLine; i <= endLine; i++) {
    const line = state.doc.line(i);
    if (allPrefixed) {
      changes.push({ from: line.from, to: line.from + prefix.length, insert: "" });
    } else {
      if (!line.text.startsWith(prefix)) {
        changes.push({ from: line.from, to: line.from, insert: prefix });
      }
    }
  }

  view.dispatch({ changes });
  view.focus();
}

export function insertTable(view: EditorView, rows: number, cols: number) {
  const colSpec = "c".repeat(cols).split("").join(" ");
  const headerCells = Array.from({ length: cols }, (_, i) => `Col ${i + 1}`).join(" & ");
  const bodyRow = Array.from({ length: cols }, () => " ").join(" & ");
  const bodyRows = Array.from({ length: rows - 1 }, () => `  ${bodyRow} \\\\`).join("\n");

  const snippet = [
    `\\begin{table}[h]`,
    `  \\centering`,
    `  \\begin{tabular}{${colSpec}}`,
    `    \\hline`,
    `    ${headerCells} \\\\`,
    `    \\hline`,
    bodyRows,
    `    \\hline`,
    `  \\end{tabular}`,
    `  \\caption{Caption}`,
    `  \\label{tab:label}`,
    `\\end{table}`,
  ].join("\n");

  insertSnippet(view, snippet);
}
